import type { AcknowledgementProgressDto } from "@/generated/model";
import { trackingTiles } from "@/lib/acknowledgements/tracking";
import {
  cardStatusFromCount,
  completionCardStatus,
} from "@/lib/dashboard/card-status";
import { DASHBOARD_STATUS_META } from "@/lib/dashboard/traffic-light";

interface AcknowledgementTrackingTilesProps {
  progress: AcknowledgementProgressDto;
}

/**
 * The five summary tiles for one published version (AC25).
 *
 * Only two tiles carry a traffic-light colour: Outstanding (red while anyone is
 * still to sign) and Completion (green only at 100%). Staff Required, Read and
 * Signed stay neutral, because they are counts rather than judgements — making
 * "3 read" green would imply 3 is a good number without knowing the
 * denominator.
 *
 * Uses the dashboard's tile vocabulary (DASHBOARD_STATUS_META) rather than the
 * row-level StatusPill vocabulary. Those are deliberately separate scales and
 * mixing them in one component makes a colour mean two things.
 */
export function AcknowledgementTrackingTiles({
  progress,
}: AcknowledgementTrackingTilesProps) {
  const tiles = trackingTiles(progress);

  return (
    <div
      className="grid w-full grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
      data-testid="ack-tracking-tiles"
    >
      {tiles.map((tile) => {
        const status =
          tile.key === "outstanding"
            ? cardStatusFromCount(tile.value)
            : tile.key === "completion"
              ? completionCardStatus(tile.value)
              : null;
        const meta = status ? DASHBOARD_STATUS_META[status] : null;

        return (
          <div
            key={tile.key}
            className={`flex flex-col items-start gap-1 rounded-md border p-4 ${
              meta
                ? `${meta.tintClass} ${meta.borderClass}`
                : "border-neutral-200"
            }`}
            data-testid={`ack-tile-${tile.key}`}
          >
            <span className="text-caption font-caption text-subtext-color">
              {tile.label}
            </span>
            <span
              className={`text-heading-2 font-heading-2 ${meta ? meta.textClass : "text-default-font"}`}
            >
              {tile.isPercentage ? `${tile.value}%` : tile.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}
